import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type Paginado } from '../api/client';
import { Tag } from '../components/Tag';
import {
  CampoBusca,
  Carregando,
  ErroBox,
  Modal,
  Secao,
  Paginacao,
  Vazio,
} from '../components/ui';

interface Registro {
  id: string;
  action: string;
  entityType: string;
  entityId: string | null;
  actorType: string;
  userId: string | null;
  usuario: { id: string; name: string; email: string } | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  ip: string | null;
  userAgent: string | null;
  createdAt: string;
}

/** Verbo da ação → cor. O sufixo `.recusado` sempre ganha o vermelho. */
function corDaAcao(action: string): string {
  if (action.endsWith('.recusado')) return 'danger';
  if (action === 'tenant.bypass') return 'warning';

  const verbo = action.split('.')[1] ?? '';
  if (verbo.startsWith('criar') || verbo === 'convidar' || verbo === 'conectar') return 'success';
  if (verbo.startsWith('excluir') || verbo === 'desconectar') return 'danger';
  if (verbo.startsWith('alterar')) return 'info';
  return 'muted';
}

const ENTIDADES = [
  'Event',
  'Participant',
  'User',
  'Organization',
  'WhatsappAccount',
  'ParticipantImport',
];

export function ConfigAuditoria() {
  const [pagina, setPagina] = useState(1);
  const [acao, setAcao] = useState('');
  const [entidade, setEntidade] = useState('');
  const [detalhe, setDetalhe] = useState<Registro | null>(null);

  const params = new URLSearchParams({ page: String(pagina), perPage: '30' });
  if (acao) params.set('action', acao);
  if (entidade) params.set('entityType', entidade);

  const consulta = useQuery({
    queryKey: ['auditoria', pagina, acao, entidade],
    queryFn: () => api<Paginado<Registro>>(`/audit?${params}`),
    // Rastro é append-only: o que já foi lido não muda.
    staleTime: 60_000,
  });

  return (
    <Secao
        titulo="Auditoria"
        descricao="Quem fez o quê, quando. Registro somente-leitura — não há como editar nem apagar."
      >

      <div className="barra-filtros">
        <CampoBusca
          valor={acao}
          aoMudar={(v) => {
            setAcao(v);
            setPagina(1);
          }}
          placeholder="Filtrar por ação (ex.: evento, participante.excluir)…"
        />
        <select
          className="form-select"
          style={{ width: 'auto' }}
          value={entidade}
          onChange={(e) => {
            setEntidade(e.target.value);
            setPagina(1);
          }}
        >
          <option value="">Todas as entidades</option>
          {ENTIDADES.map((e) => (
            <option key={e} value={e}>
              {e}
            </option>
          ))}
        </select>
      </div>

      {consulta.isPending && <Carregando />}
      {consulta.isError && <ErroBox erro={consulta.error} aoTentar={() => consulta.refetch()} />}

      {consulta.data &&
        (consulta.data.data.length === 0 ? (
          <Vazio
            icone="ti-history"
            titulo="Nenhum registro"
            descricao={
              acao || entidade
                ? 'Nenhuma ação corresponde a estes filtros.'
                : 'As ações passam a aparecer aqui conforme forem acontecendo.'
            }
          />
        ) : (
          <div className="card" style={{ padding: 0 }}>
            <div className="tabela-envolvente">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Quando</th>
                    <th>Ação</th>
                    <th>Entidade</th>
                    <th>Quem</th>
                    <th>Origem</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {consulta.data.data.map((r) => (
                    <tr key={r.id}>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {new Date(r.createdAt).toLocaleString('pt-BR')}
                      </td>
                      <td>
                        <Tag type={corDaAcao(r.action)}>{r.action}</Tag>
                      </td>
                      <td>
                        <div className="celula-principal">{r.entityType}</div>
                        {r.entityId && (
                          <div className="celula-secundaria mono">{r.entityId.slice(0, 12)}…</div>
                        )}
                      </td>
                      <td>
                        {r.usuario ? (
                          <>
                            <div className="celula-principal">{r.usuario.name}</div>
                            <div className="celula-secundaria">{r.usuario.email}</div>
                          </>
                        ) : r.actorType === 'system' ? (
                          <span style={{ color: 'var(--text-muted)' }}>sistema</span>
                        ) : (
                          // O userId sobrevive à exclusão da conta de propósito:
                          // apagar o autor não pode apagar o rastro.
                          <span style={{ color: 'var(--text-dim)' }}>conta removida</span>
                        )}
                      </td>
                      <td className="mono" style={{ fontSize: 11 }}>
                        {r.ip ?? '—'}
                      </td>
                      <td>
                        <div className="acoes-linha">
                          {(r.before || r.after) && (
                            <button
                              className="icon-btn"
                              title="Ver detalhes"
                              onClick={() => setDetalhe(r)}
                            >
                              <i className="ti ti-eye" style={{ fontSize: 15 }} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Paginacao meta={consulta.data.meta} aoMudar={setPagina} />
          </div>
        ))}

      {detalhe && (
        <Modal
          titulo={detalhe.action}
          descricao={`${detalhe.entityType}${detalhe.entityId ? ` · ${detalhe.entityId}` : ''} · ${new Date(
            detalhe.createdAt,
          ).toLocaleString('pt-BR')}`}
          aoFechar={() => setDetalhe(null)}
          largura={640}
        >
          {detalhe.before && (
            <>
              <div className="secao-titulo">
                <i className="ti ti-arrow-left" /> Antes
              </div>
              <Json valor={detalhe.before} />
            </>
          )}

          <div className="secao-titulo" style={{ marginTop: detalhe.before ? 18 : 0 }}>
            <i className="ti ti-arrow-right" /> {detalhe.before ? 'Depois' : 'Dados'}
          </div>
          <Json valor={detalhe.after} />

          {detalhe.userAgent && (
            <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 16, lineHeight: 1.5 }}>
              {detalhe.userAgent}
            </div>
          )}

          <div className="aviso" style={{ marginTop: 16 }}>
            <i className="ti ti-shield-lock" />
            <span>
              Senhas, tokens e app secrets aparecem como <code>[redigido]</code> — o valor
              nunca chega a ser gravado.
            </span>
          </div>
        </Modal>
      )}
    </Secao>
  );
}

function Json({ valor }: { valor: unknown }) {
  return (
    <pre
      className="mono"
      style={{
        background: 'var(--bg-elevated)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-sm)',
        padding: 12,
        margin: 0,
        maxHeight: 260,
        overflow: 'auto',
        fontSize: 11.5,
        lineHeight: 1.5,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      }}
    >
      {valor === null || valor === undefined ? '—' : JSON.stringify(valor, null, 2)}
    </pre>
  );
}
